import { useState, useMemo } from "react";
import { Slider } from "@/components/ui/slider";
import { motion } from "framer-motion";

const gbp = (n) => "£" + Math.round(n).toLocaleString("en-GB");

export const NetbackCalculator = () => {
  const [tonnage, setTonnage] = useState([120]);
  const [current, setCurrent] = useState([15]);
  const [improved, setImproved] = useState([55]);

  const { curMonthly, potMonthly, annualImp } = useMemo(() => {
    const t = tonnage[0];
    const curMonthly = t * current[0];
    const potMonthly = t * improved[0];
    return { curMonthly, potMonthly, annualImp: (potMonthly - curMonthly) * 12 };
  }, [tonnage, current, improved]);

  const rows = [
    { label: "Monthly tonnage", value: `${tonnage[0]} t`, state: tonnage, set: setTonnage, min: 20, max: 500, step: 5, unit: "t", testid: "calc-slider-tonnage" },
    { label: "Current recovered value", value: `£${current[0]}/t`, state: current, set: setCurrent, min: 0, max: 120, step: 1, unit: "£/t", testid: "calc-slider-current" },
    { label: "Potential improved netback", value: `£${improved[0]}/t`, state: improved, set: setImproved, min: 0, max: 160, step: 1, unit: "£/t", testid: "calc-slider-improved" },
  ];

  return (
    <div data-testid="netback-calculator" className="grid grid-cols-1 lg:grid-cols-2 border border-dc-border bg-dc-surface">
      <div className="p-6 lg:p-10 border-b lg:border-b-0 lg:border-r border-dc-border">
        <div className="dc-label mb-6">Illustrative calculation only</div>
        <div className="space-y-8">
          {rows.map((r) => (
            <div key={r.label}>
              <div className="flex justify-between items-baseline mb-3">
                <span className="text-sm text-dc-text2">{r.label}</span>
                <span className="font-mono text-dc-accent text-lg">{r.value}</span>
              </div>
              <Slider
                data-testid={r.testid}
                value={r.state}
                onValueChange={r.set}
                min={r.min}
                max={r.max}
                step={r.step}
                className="[&_[role=slider]]:rounded-none [&_[role=slider]]:border-dc-accent [&_[role=slider]]:bg-dc-accent"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="p-6 lg:p-10 flex flex-col justify-center bg-dc-bg/40">
        <div className="space-y-6">
          <div className="flex justify-between items-baseline border-b border-dc-border pb-4">
            <span className="text-sm text-dc-text2">Current monthly recovered value</span>
            <span data-testid="calc-current-monthly" className="font-display text-2xl">{gbp(curMonthly)}</span>
          </div>
          <div className="flex justify-between items-baseline border-b border-dc-border pb-4">
            <span className="text-sm text-dc-text2">Potential monthly recovered value</span>
            <span data-testid="calc-potential-monthly" className="font-display text-2xl">{gbp(potMonthly)}</span>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-dc-muted mb-2">Potential annual improvement</div>
            <motion.div
              key={annualImp}
              initial={{ opacity: 0.4 }}
              animate={{ opacity: 1 }}
              data-testid="calc-annual-improvement"
              className="font-display text-4xl lg:text-5xl font-semibold text-dc-accent"
            >
              {annualImp >= 0 ? gbp(annualImp) : "—"}
            </motion.div>
          </div>
        </div>
        <p className="text-[11px] text-dc-muted leading-relaxed mt-8">
          Actual recovered-material values fluctuate and depend on grade, contamination, presentation, location,
          logistics and market conditions. This tool is illustrative and does not constitute a commercial offer.
        </p>
      </div>
    </div>
  );
};
